// src/pages/Documents/ListDocuments.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import DataTableWithFilters from "../../../components/Common/DataTableWithFilters";
import EditDocuments from "./EditDocuments";
import EditDocumentSummary from "./EditDocumentSummary";
import ManageCategories from "./ManageCategories";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Modal,
  Button,
  useTheme,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';

const ensureUrlProtocol = (url) => {
  if (!url) return "";
  if (!/^(https?:\/\/|mailto:|tel:)/i.test(url)) {
    return "https://" + url;
  }
  return url;
};

const ExpandedComponent = ({ data }) => {
  const theme = useTheme();

  return (
    <Box p={2} sx={{ backgroundColor: theme.palette.action.hover }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>File Name:</strong> {data.filename}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Summary:</strong>
      </Typography>
      {data.summary ? (
        <Box
          sx={{ mb: 1, pl: 2 }}
          dangerouslySetInnerHTML={{ __html: data.summary }}
        />
      ) : (
        <Typography variant="body2" sx={{ mb: 1, pl: 2 }}>N/A</Typography>
      )}
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Page Content (HTML):</strong>
      </Typography>
      <Box
        sx={{ mb: 1, pl: 2 }}
        dangerouslySetInnerHTML={{ __html: data.page_content }}
      />
      <Typography variant="body2">
        <strong>Document Source:</strong>{" "}
        {data.website_link && data.website_link !== "#" ? (
          <a
            href={ensureUrlProtocol(data.website_link)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {data.website_link}
          </a>
        ) : (
          "N/A"
        )}
      </Typography>
    </Box>
  );
};

export default function ListDocuments({ refresh, chatbotId, permission, categories = [], onCategoriesUpdate }) {
  const theme = useTheme();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);

  // --- State for filters ---
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [tagsFilter, setTagsFilter] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [trainFilter, setTrainFilter] = useState('all');
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  // --- State for the training button ---
  const [isTraining, setIsTraining] = useState(false);
  const [trainError, setTrainError] = useState("");
  const [trainSuccess, setTrainSuccess] = useState("");

  useEffect(() => {
    document.title = "Uploaded Files | KlonIT Web Application";
    fetchDocuments();
  }, [refresh, triggerFetch]);

  // Effect to extract all unique tags from documents
  useEffect(() => {
    const tags = new Set();
    documents.forEach(doc => {
      if (doc.tags) {
        doc.tags.split(',').forEach(tag => {
          const trimmedTag = tag.trim();
          if (trimmedTag) {
            tags.add(trimmedTag);
          }
        });
      }
    });
    setAllTags(Array.from(tags).sort());
  }, [documents]);

  // Main filtering logic
  useEffect(() => {
    const filtered = documents.filter((doc) => {
      // 1. Search text filter
      const matchesSearch = Object.values(doc)
        .concat(doc.categories?.map(c => c.name) || [])
        .join(" ")
        .toLowerCase()
        .includes(searchText.toLowerCase());

      // 2. Category filter (multi-select)
      const matchesCategory = categoryFilter.length > 0
        ? doc.categories?.some(cat => categoryFilter.includes(cat.id))
        : true;

      // 3. Tags filter (multi-select)
      const matchesTags = tagsFilter.length > 0
        ? tagsFilter.every(tag =>
            doc.tags?.toLowerCase().includes(tag.toLowerCase())
          )
        : true;

      // 4. Train filter
      const matchesTrain = trainFilter === 'all'
        ? true
        : trainFilter === 'trained'
            ? doc.is_trained
            : !doc.is_trained;

      return matchesSearch && matchesCategory && matchesTags && matchesTrain;
    });
    setFilteredDocuments(filtered);
  }, [searchText, categoryFilter, tagsFilter, trainFilter, documents]);


  const fetchDocuments = async () => {
    setLoading(true);
    setError("");
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const response = await axiosInstance.get(`/get-private-data`, { params });
      const responseData =
        typeof response.data.data === "string"
          ? JSON.parse(response.data.data)
          : response.data.data;

      setDocuments(responseData);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setError("Failed to fetch documents.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (
      !window.confirm(
        `Are you sure you want to delete document ID ${documentId}? This cannot be undone.`
      )
    )
      return;

    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      await axiosInstance.delete(`/delete-private-data/${documentId}`, {
        params,
      });
      setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      setError("Document has been deleted successfully.");
    } catch (error) {
      console.error("Error deleting document:", error);
      setError("Failed to delete document.");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocuments.length === 0) {
      setError("No documents selected for deletion.");
      return;
    }
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedDocuments.length} selected document(s)?`
      )
    )
      return;

    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      await Promise.all(
        selectedDocuments.map((doc) =>
          axiosInstance.delete(`/delete-private-data/${doc.id}`, { params })
        )
      );

      const remainingDocs = documents.filter(
        (doc) => !selectedDocuments.some((selected) => selected.id === doc.id)
      );
      setDocuments(remainingDocs);
      setSelectedDocuments([]);
      setToggleCleared(!toggleCleared);
      setError("Selected documents have been deleted successfully.");
    } catch (error) {
      console.error("Error deleting documents:", error);
      setError("Failed to delete documents.");
    }
  };

  const handleTrain = async () => {
    setIsTraining(true);
    setTrainError("");
    setTrainSuccess("");

    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      await axiosInstance.get(`/retrain-chatbot`, { params });

      setTrainSuccess("Chatbot retraining initiated successfully!");

      setToggleCleared(!toggleCleared);
      setSelectedDocuments([]);

      setTriggerFetch(prev => !prev);

    } catch (error) {
      console.error("Error in retraining the chatbot:", error);
      setTrainError(error.response?.data?.message || "An error occurred while retraining the chatbot.");
    } finally {
      setIsTraining(false);
    }
  };

  const handleEdit = (document) => {
    setCurrentDocument(document);
    setIsModalOpen(true);
  };

  const handleEditSummary = (document) => {
    setCurrentDocument(document);
    setIsSummaryModalOpen(true);
  };

  const closeSummaryModal = () => {
    setIsSummaryModalOpen(false);
    setCurrentDocument(null);
  };

  const updateFromSummaryModal = () => {
    fetchDocuments();
    closeSummaryModal();
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentDocument(null);
  };

  const updateModal = () => {
    fetchDocuments();
    closeModal();
  };

  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
        }`,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "14px",
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
        }`,
      },
    },
    expanderRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
      },
    },
  };

  const columns = [
    {
      name: "File Name",
      selector: (row) => row.filename,
      sortable: true,
      minWidth: "250px",
      grow: 2,
      wrap: true,
    },
    {
      name: "Category",
      selector: (row) => row.categories?.map(cat => cat.name).join(', ') || 'Uncategorized',
      sortable: true,
      wrap: true,
      minWidth: "130px",
    },
    {
      name: "Tag",
      selector: (row) => row.tags,
      sortable: false,
      wrap: true,
      minWidth: "150px",
    },
    {
      name: "Is Trained?",
      selector: (row) => (row.is_trained ? "Yes" : "No"),
      sortable: true,
      maxWidth: "110px",
    },
    {
      name: "Web Link",
      cell: (row) =>
        row.website_link && row.website_link !== "#" ? (
          <a
            href={ensureUrlProtocol(row.website_link)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: theme.palette.primary.main, wordBreak: "break-all" }}
          >
            {row.website_link.length > 30 ? row.website_link.substring(0, 30) + "..." : row.website_link}
          </a>
        ) : (
          <Typography variant="body2" color="textSecondary">—</Typography>
        ),
      sortable: false,
      minWidth: "150px",
    },
    {
      name: "Action",
      cell: (row) => (
        <Box display="flex" gap={1}>
          {permission.canModify && (
            <>
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleEdit(row)}
                title="Edit Document Content"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleEditSummary(row)}
                title="Edit Summary"
              >
                <DescriptionIcon />
              </IconButton>
            </>
          )}
          {permission.canDelete && (
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDelete(row.id)}
              title="Delete Document"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
      ignoreRowClick: true,
      width: "150px",
    },
  ];

  const handleSelectedRowsChange = ({ selectedRows }) =>
    setSelectedDocuments(selectedRows);

  const filterComponents = (
    <>
      {/* 1. Manage Categories Button */}
      {permission.canModify && (
        <Button
          variant="outlined"
          onClick={() => setIsCategoryModalOpen(true)}
        >
          Manage Categories
        </Button>
      )}

      {/* 2. Category Filter */}
      <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 }, maxWidth: { xs: '100%', sm: 300 } }}>
        <InputLabel>Categories</InputLabel>
        <Select
          multiple
          value={categoryFilter}
          label="Categories"
          onChange={(e) => setCategoryFilter(e.target.value)}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => {
                const category = categories.find(c => c.id === value);
                return <Chip key={value} label={category ? category.name : ''} size="small" />;
              })}
            </Box>
          )}
        >
          {categories.map(cat => (
            <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* 3. Tags Filter */}
      <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 }, maxWidth: { xs: '100%', sm: 300 } }}>
        <InputLabel>Filter by Tags</InputLabel>
        <Select
          multiple
          value={tagsFilter}
          label="Filter by Tags"
          onChange={(e) => setTagsFilter(e.target.value)}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
          {allTags.map(tag => (
            <MenuItem key={tag} value={tag}>{tag}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* 4. Trained Filter */}
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Trained</InputLabel>
        <Select
          value={trainFilter}
          label="Trained"
          onChange={(e) => setTrainFilter(e.target.value)}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="trained">Yes</MenuItem>
          <MenuItem value="not_trained">No</MenuItem>
        </Select>
      </FormControl>
    </>
  );

  const selectedRowsActions = (
    <Stack direction="row" spacing={2}>
      {permission.canDelete && (
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleBulkDelete}
            startIcon={<DeleteIcon />}
            disabled={isTraining}
          >
            Bulk Delete
          </Button>
      )}
      {permission.canModify && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleTrain}
            disabled={isTraining}
            startIcon={isTraining ? <CircularProgress size={20} color="inherit" /> : <ModelTrainingIcon />}
          >
            {isTraining ? "Training..." : "Train Chatbot"}
          </Button>
      )}
    </Stack>
  );

  return (
    <>
      <DataTableWithFilters
        columns={columns}
        data={filteredDocuments}
        loading={loading}
        error={error}
        onErrorClose={() => setError("")}
        success={trainSuccess}
        onSuccessClose={() => setTrainSuccess("")}
        filterComponents={filterComponents}
        selectedRowsActions={selectedRowsActions}
        selectedRowsCount={selectedDocuments.length}
        onSelectedRowsChange={handleSelectedRowsChange}
        clearSelectedRows={toggleCleared}
        expandableRows
        expandableRowsComponent={ExpandedComponent}
        noDataMessage="No documents found matching your criteria"
        theme={theme}
        customStyles={customDataTableStyles}
        searchText={searchText}
        onSearchChange={(e) => setSearchText(e.target.value)}
        searchPlaceholder="Search documents..."
      />

      {trainError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setTrainError("")}>
          {trainError}
        </Alert>
      )}

      {/* Edit Document Modal */}
      <Modal
        open={isModalOpen}
        onClose={closeModal}
        aria-labelledby="edit-document-modal"
        closeAfterTransition
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "80%", md: "900px" },
            maxWidth: "90vw",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            p: 0,
            display: "flex",
            flexDirection: "column",
            outline: "none",
            overflow: "auto",
          }}
        >
          {currentDocument && (
            <EditDocuments
              document={currentDocument}
              onClose={closeModal}
              onUpdate={updateModal}
              chatbotId={chatbotId}
              permission={permission}
            />
          )}
        </Box>
      </Modal>

      {/* Edit Summary Modal */}
      <Modal
        open={isSummaryModalOpen}
        onClose={closeSummaryModal}
        aria-labelledby="edit-summary-modal"
        closeAfterTransition
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "70%", md: "700px" },
            maxWidth: "90vw",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            p: 0,
            display: "flex",
            flexDirection: "column",
            outline: "none",
            overflow: "auto",
          }}
        >
          {currentDocument && (
            <EditDocumentSummary
              document={currentDocument}
              onClose={closeSummaryModal}
              onUpdate={updateFromSummaryModal}
              chatbotId={chatbotId}
              permission={permission}
            />
          )}
        </Box>
      </Modal>

      {/* Render the ManageCategories Modal */}
      {isCategoryModalOpen && (
        <ManageCategories
          open={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          chatbotId={chatbotId}
          onCategoriesUpdate={() => {
            onCategoriesUpdate();
            fetchDocuments();
          }}
        />
      )}
    </>
  );
}